export type DashboardPeriodKey = '1h' | 'today' | '24h' | '7' | '30' | '90' | 'custom_datetime'

export function getDashboardPeriodLabel(period: DashboardPeriodKey): string {
  switch (period) {
    case '1h':
      return 'последний час'
    case 'today':
      return 'сегодня (с 00:00 по местному времени)'
    case '24h':
      return 'последние сутки'
    case '7':
      return 'последние 7 дней'
    case '30':
      return 'последние 30 дней'
    case '90':
      return 'последние 90 дней'
    case 'custom_datetime':
      return 'выбранный период'
    default:
      return 'период'
  }
}

export type DashboardPeriodParams = {
  hours?: number
  days?: number
  date_time_from?: string
  date_time_to?: string
  project_id?: string
  exclude_burst?: boolean
  /** Не кэшировать на клиенте для «скользящих» окон */
  _t?: number
}

export function buildDashboardPeriodParams(
  period: DashboardPeriodKey,
  opts: {
    projectId?: string
    customFromIso?: string | null
    customToIso?: string | null
    excludeBurst?: boolean
  } = {},
): DashboardPeriodParams {
  const out: DashboardPeriodParams = {}
  if (opts.projectId) out.project_id = opts.projectId
  if (opts.excludeBurst) out.exclude_burst = true

  if (period === '1h') {
    out.hours = 1
    out._t = Date.now()
  } else if (period === 'today') {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    out.date_time_from = start.toISOString()
    out.date_time_to = new Date().toISOString()
    out._t = Date.now()
  } else if (period === '24h') {
    out.hours = 24
    out._t = Date.now()
  } else if (period === 'custom_datetime') {
    if (opts.customFromIso) out.date_time_from = opts.customFromIso
    if (opts.customToIso) out.date_time_to = opts.customToIso
  } else {
    const d = parseInt(period, 10)
    if (!Number.isNaN(d)) out.days = d
  }

  return out
}

function deriveRangeFromParams(params: DashboardPeriodParams): { start: Date; end: Date } | null {
  const now = new Date(params._t ?? Date.now())
  if (params.date_time_from || params.date_time_to) {
    const start = params.date_time_from ? new Date(params.date_time_from) : null
    const end = params.date_time_to ? new Date(params.date_time_to) : now
    if (start && !Number.isNaN(start.getTime()) && end && !Number.isNaN(end.getTime()) && end > start) {
      return { start, end }
    }
    return null
  }
  if (typeof params.hours === 'number' && params.hours > 0) {
    const end = now
    const start = new Date(end.getTime() - params.hours * 60 * 60 * 1000)
    return { start, end }
  }
  if (typeof params.days === 'number' && params.days > 0) {
    const end = now
    const start = new Date(end.getTime() - params.days * 24 * 60 * 60 * 1000)
    return { start, end }
  }
  return null
}

/** Предыдущий интервал той же длительности (для сравнения на графиках). */
export function getPreviousPeriodParams(params: DashboardPeriodParams): DashboardPeriodParams | null {
  const range = deriveRangeFromParams(params)
  if (!range) return null
  const durationMs = range.end.getTime() - range.start.getTime()
  if (!(durationMs > 0)) return null
  const prevStart = new Date(range.start.getTime() - durationMs)
  const prevEnd = range.start
  const prev: DashboardPeriodParams = {
    date_time_from: prevStart.toISOString(),
    date_time_to: prevEnd.toISOString(),
  }
  if (params.project_id) prev.project_id = params.project_id
  if (params.exclude_burst) prev.exclude_burst = true
  return prev
}

export function periodComparisonHint(current: number, previous: number, inverse = false): string {
  const cur = Number(current)
  const prev = Number(previous)
  if (!Number.isFinite(cur) || !Number.isFinite(prev)) {
    return 'Сравнение с предыдущим периодом: нет данных'
  }
  if (prev === 0) {
    if (cur === 0) return 'Сравнение с предыдущим периодом: без изменений'
    return 'Сравнение с предыдущим периодом: стало больше, база 0'
  }
  const rawDiff = cur - prev
  const pct = Math.abs((rawDiff / Math.abs(prev)) * 100)
  if (Math.abs(rawDiff) < 1e-9) return 'Сравнение с предыдущим периодом: без изменений'
  const became = rawDiff > 0 ? 'стало больше' : 'стало меньше'
  const isPositive = inverse ? rawDiff < 0 : rawDiff > 0
  const tone = isPositive ? 'положительно' : 'отрицательно'
  return `Сравнение с предыдущим периодом: ${became} на ${pct.toFixed(1)}% (${tone})`
}


/** Freeze one range for every tab and its adjacent comparison interval. */
export function resolveDashboardPeriodParams(params: DashboardPeriodParams): DashboardPeriodParams {
  if (params.date_time_from || params.date_time_to) return params
  const durationMs = params.hours != null ? params.hours * 3600000 : params.days != null ? params.days * 86400000 : 0
  if (durationMs <= 0) return params
  const bucketMs = params.hours != null ? (params.hours <= 1 ? 60000 : params.hours <= 24 ? 120000 : 600000) : (params.days! >= 30 ? 1800000 : 600000)
  const endMs = Math.floor((params._t ?? Date.now()) / bucketMs) * bucketMs
  const { hours, days, ...rest } = params
  return { ...rest, date_time_from: new Date(endMs - durationMs).toISOString(), date_time_to: new Date(endMs).toISOString() }
}
